// create-holiday-dialog.component.ts

import { Component, Inject, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import * as moment from 'moment';

import { SuccessDialogComponent } from '../success-dialog/success-dialog';
import { Country, State } from '../../models/leave.model';
import { LeaveService } from '../../services/leave.service';
import { parseLocalDate } from '../../utils/date.utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSearchableSelectComponent } from '@rcs/ers-elementals';

export interface CreateHolidayDialogData {
  holiday?: {
    id: number;
    name: string;
    date: string;
    isoDate?: string;  // YYYY-MM-DD — preferred for safe Date reconstruction
    day: string;
    type: string;
    optional: string;
    countryId?: string | null;
    stateId?: string | null;
    notes?: string | null;
  };
}

/** Shape returned to the parent after dialog close */
export interface CreateHolidayDialogResult {
  name: string;
  /** YYYY-MM-DD string — timezone-safe, always matches what the user picked */
  isoDate: string;
  day: string;
  isOptional: boolean;
  applicableToCountryId?: string;
  applicableToStateId?: string;
  notes?: string;
}

@Component({
  selector: 'ers-create-holiday-dialog',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    MatButtonModule,
    ErsSearchableSelectComponent
  ],
  templateUrl: './create-holiday-dialog.component.html',
  styleUrls: ['./create-holiday-dialog.component.scss'],
})
export class CreateHolidayDialogComponent implements OnInit {
  form: FormGroup;
  isEditMode = false;

  countries: Country[] = [];
  states: State[] = [];
  loadingCountries = false;
  loadingStates = false;

  /** True while the initial country/state is being loaded in edit mode.
   *  Prevents the valueChanges handler from clobbering the pre-selected state. */
  private _initializing = true;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateHolidayDialogComponent>,
    private dialog: MatDialog,
    private leaveService: LeaveService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: CreateHolidayDialogData,
  ) {
    this.isEditMode = !!(data?.holiday);
    let initialDate: moment.Moment | null = null;
    if (this.isEditMode && data?.holiday) {
      const iso = data.holiday.isoDate ?? '';
      if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        // Use moment.utc to match the global MomentDateAdapter (useUtc: true).
        // This ensures the datepicker displays the exact calendar date stored in the DB.
        initialDate = moment.utc(iso, 'YYYY-MM-DD');
      } else if (data.holiday.date) {
        const parsed = parseLocalDate(data.holiday.date);
        if (!isNaN(parsed.getTime())) {
          // Build UTC moment from local date parts to avoid timezone shift
          initialDate = moment.utc([parsed.getFullYear(), parsed.getMonth(), parsed.getDate()]);
        }
      }
    }
    this.form = this.fb.group({
      name:             [data?.holiday?.name ?? '', Validators.required],
      date:             [initialDate,              Validators.required],
      isOptional:       [data?.holiday?.optional === 'Optional'],
      countryId:        [data?.holiday?.countryId ?? null, Validators.required],
      stateId:          [data?.holiday?.stateId ?? null],
      notes:            [data?.holiday?.notes ?? ''],
    });
  }

  ngOnInit(): void {
    // Reload states whenever country selection changes (user-driven only,
    // guarded by _initializing so the initial state load isn't clobbered)
    this.form.get('countryId')!.valueChanges.subscribe((countryId: string | null) => {
      if (!this._initializing) {
        // Only reset state when the user actually changes the country
        this.form.get('stateId')!.setValue(null, { emitEvent: false });
        this.states = [];
      }
      if (!countryId) return;
      this.loadingStates = true;
      this.leaveService.getStatesByCountry(countryId).subscribe({
        next: (data) => {
          this.states = data;
          this.loadingStates = false;
        },
        error: () => { this.loadingStates = false; },
      });
    });

    this.loadingCountries = true;
    this.leaveService.getCountries().subscribe({
      next: (countriesData) => {
        this.countries = countriesData;
        this.loadingCountries = false;

        // In edit mode, pre-load states for the existing country and then
        // restore the pre-selected stateId once the options are available.
        const editCountryId = this.data?.holiday?.countryId;
        if (this.isEditMode && editCountryId) {
          this.loadingStates = true;
          this.leaveService.getStatesByCountry(editCountryId).subscribe({
            next: (states) => {
              this.states = states;
              this.loadingStates = false;
              const editStateId = this.data?.holiday?.stateId;
              if (editStateId) {
                this.form.get('stateId')!.setValue(editStateId, { emitEvent: false });
              }
              // Initialization complete — subsequent countryId changes are user-driven
              this._initializing = false;
            },
            error: () => {
              this.loadingStates = false;
              this._initializing = false;
            },
          });
        } else {
          // No pre-existing country — initialization is done immediately
          this._initializing = false;
        }
      },
      error: () => {
        this.loadingCountries = false;
        this._initializing = false;
      },
    });
  }

  onReset(): void {
    this.form.reset({
      name: '', date: null, isOptional: false,
      countryId: null, stateId: null, notes: '',
    });
    this.states = [];
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.value;
    const rawDate = v.date;

    // DEBUG: trace exactly what the datepicker produced
    console.log('[CreateHolidayDialog] rawDate type:', typeof rawDate, 'isMoment:', !!(rawDate?.format), 'value:', rawDate);

    // Extract YYYY-MM-DD in a timezone-safe way.
    // The global DateAdapter is MomentDateAdapter with useUtc:true,
    // so the form control value is a UTC Moment object.
    let isoDate: string;
    if (rawDate && typeof rawDate === 'object' && typeof rawDate.format === 'function') {
      // Moment object — use .format() which respects the Moment's UTC/local mode
      isoDate = rawDate.format('YYYY-MM-DD');
    } else if (rawDate instanceof Date) {
      // Native Date fallback — use local date parts (safe for IST+)
      isoDate = `${rawDate.getFullYear()}-${String(rawDate.getMonth() + 1).padStart(2, '0')}-${String(rawDate.getDate()).padStart(2, '0')}`;
    } else if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
      isoDate = rawDate.substring(0, 10);
    } else {
      this.form.get('date')!.setErrors({ invalidDate: true });
      return;
    }
    console.log('[CreateHolidayDialog] extracted isoDate:', isoDate);

    // Validate the extracted date
    const [yr, mo, dy] = isoDate.split('-').map(Number);
    const checkDate = new Date(yr, mo - 1, dy);
    if (isNaN(checkDate.getTime())) {
      this.form.get('date')!.setErrors({ invalidDate: true });
      return;
    }

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // Use UTC day since the Moment is UTC-based
    const dayIndex = moment.utc(isoDate, 'YYYY-MM-DD').day();

    const result: CreateHolidayDialogResult = {
      name:                 v.name,
      isoDate,
      day:                  days[dayIndex],
      isOptional:           !!v.isOptional,
      applicableToCountryId: v.countryId ?? undefined,
      applicableToStateId:   v.stateId ?? undefined,
      notes:                v.notes ?? undefined,
    };

    this.dialogRef.close(result);

    if (!this.isEditMode) {
      this.dialog.open(SuccessDialogComponent, {
        width: '600px',
        panelClass: 'lm-dialog-panel',
        data: {
          title: 'New holiday has been added!',
          desc:  'All employees can now view the newly added holiday under the Company Holidays tab.',
        },
      });
    }
  }
}
